import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../../utils/jobIntake';

// Shared building blocks for the Organization screens (approved mockups: Inter, #0066CC / #003D82, slate text,
// #E6E9F0 borders, 10px cards, pill tags). Keyboard and screen-reader friendly: labelled controls, Esc closes
// panels, focus moves into dialogs and back, toasts are announced.

export const card = 'bg-white border border-[#E6E9F0] rounded-[10px]';
export const btnPrimary = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#0066CC] text-white text-sm font-semibold hover:bg-[#0057ad] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC]/40';
export const btnSecondary = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E6E9F0] bg-white text-[#0F172A] text-sm font-semibold hover:bg-[#F5F7FB] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC]/40';
export const btnDanger = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50';
export const input = 'w-full px-3 py-2 text-sm border border-[#E6E9F0] rounded-lg bg-white text-[#0F172A] focus:outline-none focus:border-[#0066CC] focus:ring-2 focus:ring-[#0066CC]/15';
export const labelCls = 'block text-xs font-semibold text-[#334155] mb-1';

// JSON API helper (throws Error with the server's detail and .status)
export async function api(path, { method = 'GET', body, raw = false } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (raw) return res;
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const detail = data && data.detail;
    const msg = typeof detail === 'string' ? detail : (detail && typeof detail.message === 'string' ? detail.message : `Request failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.problems = (detail && Array.isArray(detail.problems)) ? detail.problems : null;   // e.g. workflow steps that can't resolve
    err.reference = (data && data.reference) || null;   // a 500's reference id (the server logs the details)
    err.detail = detail;                                 // structured details (e.g. what Submit still needs)
    throw err;
  }
  return data;
}

export function initials(name) {
  const parts = (name || '?').trim().split(/\s+/);
  return ((parts[0] || '')[0] || '?').toUpperCase() + ((parts.length > 1 ? parts[parts.length - 1][0] : '') || '').toUpperCase();
}

const AVATAR_COLORS = ['bg-[#E8F1FB] text-[#0066CC]', 'bg-[#E9F7EF] text-[#2F8F4E]', 'bg-[#E6F6F5] text-[#00877D]', 'bg-[#F1ECFB] text-[#6B46C1]', 'bg-[#FDF3E7] text-[#B4610E]'];
export function Avatar({ name, size = 'md' }) {
  const n = (name || '').split('').reduce((a, ch) => a + ch.charCodeAt(0), 0);
  const sz = size === 'sm' ? 'w-7 h-7 text-[11px]' : 'w-9 h-9 text-xs';
  return <span aria-hidden="true" className={`${sz} ${AVATAR_COLORS[n % AVATAR_COLORS.length]} rounded-full inline-flex items-center justify-center font-bold shrink-0`}>{initials(name)}</span>;
}

export function Tag({ children, tone = 'blue' }) {
  const tones = { blue: 'bg-[#E8F1FB] text-[#003D82]', gray: 'bg-[#F1F5F9] text-[#475569]', green: 'bg-[#E9F7EF] text-[#2F7A47]',
    amber: 'bg-[#FEF5E7] text-[#9A5B0B]', red: 'bg-red-50 text-red-700', teal: 'bg-[#E6F6F5] text-[#00776E]' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${tones[tone] || tones.blue}`}>{children}</span>;
}

export function StatusPill({ status }) {
  const map = { active: ['Active', 'green'], invited: ['Invited', 'amber'], inactive: ['Inactive', 'gray'] };
  const [label, tone] = map[status] || [status, 'gray'];
  return <Tag tone={tone}>{label}</Tag>;
}

export function Tabs({ tabs, active, onChange, label }) {
  const refs = useRef({});
  const keys = tabs.map((t) => t.key);
  const onKey = (e) => {
    const i = keys.indexOf(active);
    let next = null;
    if (e.key === 'ArrowRight') next = keys[(i + 1) % keys.length];
    if (e.key === 'ArrowLeft') next = keys[(i - 1 + keys.length) % keys.length];
    if (next) { e.preventDefault(); onChange(next); setTimeout(() => refs.current[next] && refs.current[next].focus(), 0); }
  };
  return (
    <div role="tablist" aria-label={label} className="flex gap-6 border-b border-[#E6E9F0]" onKeyDown={onKey}>
      {tabs.map((t) => (
        <button key={t.key} ref={(el) => { refs.current[t.key] = el; }} role="tab" id={`tab-${t.key}`} aria-selected={active === t.key}
          aria-controls={`panel-${t.key}`} tabIndex={active === t.key ? 0 : -1} onClick={() => onChange(t.key)}
          className={`-mb-px pb-3 pt-1 text-sm font-semibold border-b-2 transition-colors focus:outline-none focus-visible:text-[#0066CC] ${
            active === t.key ? 'border-[#0066CC] text-[#0066CC]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function useDialogFocus(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const before = document.activeElement;
    const el = ref.current;
    const first = el && el.querySelector('input, select, textarea, button, [tabindex="0"]');
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && el) {
        const f = el.querySelectorAll('input, select, textarea, button, a[href], [tabindex="0"]');
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (before && before.focus) before.focus(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}

export function SidePanel({ open, title, subtitle, onClose, children, footer, width = 'max-w-md' }) {
  const ref = useDialogFocus(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-[#0F172A]/30" onClick={onClose} aria-hidden="true" />
      <aside ref={ref} role="dialog" aria-modal="true" aria-label={title}
        className={`relative w-full ${width} h-full bg-white shadow-2xl flex flex-col font-inter`}>
        <header className="px-6 py-5 border-b border-[#E6E9F0] flex items-start justify-between gap-4">
          <div><h2 className="text-lg font-extrabold text-[#0F172A]">{title}</h2>
            {subtitle && <p className="text-sm text-[#64748B] mt-0.5">{subtitle}</p>}</div>
          <button onClick={onClose} aria-label="Close" className="text-[#64748B] hover:text-[#0F172A] text-xl leading-none">×</button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <footer className="px-6 py-4 border-t border-[#E6E9F0] flex justify-end gap-2">{footer}</footer>}
      </aside>
    </div>
  );
}

export function Modal({ open, title, onClose, children, footer, width = 'max-w-lg' }) {
  const ref = useDialogFocus(open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0F172A]/40" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title}
        className={`relative w-full ${width} bg-white rounded-[12px] shadow-2xl font-inter max-h-[90vh] flex flex-col`}>
        <header className="px-6 pt-5 pb-3 flex items-start justify-between gap-4">
          <h2 className="text-lg font-extrabold text-[#0F172A]">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#64748B] hover:text-[#0F172A] text-xl leading-none">×</button>
        </header>
        <div className="px-6 pb-4 overflow-y-auto">{children}</div>
        {footer && <footer className="px-6 py-4 border-t border-[#E6E9F0] flex justify-end gap-2">{footer}</footer>}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', danger = false, busy = false, onConfirm, onClose }) {
  return (
    <Modal open={open} title={title} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={danger ? btnDanger : btnPrimary} disabled={busy} onClick={onConfirm}>{busy ? 'Working…' : confirmLabel}</button></>}>
      <p className="text-sm text-[#334155]">{message}</p>
    </Modal>
  );
}

const ToastCtx = createContext({ show: () => {} });
export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const show = useCallback((text, tone = 'ok') => {
    const id = Math.random().toString(36).slice(2);
    setItems((x) => [...x, { id, text, tone }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), 4500);
  }, []);
  return (
    <ToastCtx.Provider value={{ show }}>
      {children}
      <div aria-live="polite" role="status" className="fixed bottom-5 right-5 z-[60] space-y-2">
        {items.map((t) => (
          <div key={t.id} className={`px-4 py-3 rounded-lg shadow-lg text-sm font-semibold font-inter ${t.tone === 'error' ? 'bg-red-600 text-white' : 'bg-[#0F172A] text-white'}`}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

export function SkeletonRows({ rows = 5, cols = 5 }) {
  return (
    <tbody aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-t border-[#E6E9F0]">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-4 py-4"><div className="h-3 rounded bg-[#EEF1F6] animate-pulse" style={{ width: `${50 + ((i + j) % 4) * 12}%` }} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function Skeleton({ className = 'h-4 w-full' }) {
  return <div aria-hidden="true" className={`rounded bg-[#EEF1F6] animate-pulse ${className}`} />;
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="text-center py-12 px-6">
      <p className="text-sm font-bold text-[#0F172A]">{title}</p>
      {hint && <p className="text-sm text-[#64748B] mt-1">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function InlineError({ children }) {
  if (!children) return null;
  return <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{children}</p>;
}

export function Pagination({ page, pageSize, total, onPage, noun = 'people', count }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const shown = count != null ? count : Math.min(total, page * pageSize) - (total ? (page - 1) * pageSize : 0);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E6E9F0] text-sm text-[#64748B]">
      <span>Showing {shown} of {total} {noun}</span>
      <div className="flex items-center gap-1" role="navigation" aria-label="Pages">
        <button className={btnSecondary + ' !px-2.5 !py-1'} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">‹</button>
        <span className="px-2 text-[#334155]" aria-current="page">{page} / {pages}</span>
        <button className={btnSecondary + ' !px-2.5 !py-1'} disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">›</button>
      </div>
    </div>
  );
}

export function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d === 1 ? '1 day ago' : `${d} days ago`;
}
